import type { ReactNode } from 'react';
import type { DataStatus } from '@oxxean/types';
import { EmptyState, ErrorState, PartialNotice, UnavailableNotice } from './states';
import type { EmptyStateProps } from './states';

/**
 * Los CUATRO estados obligatorios de toda vista de datos (§6 de arquitectura.md):
 * carga, vacío, error con reintento y parcial con aviso de cobertura.
 *
 * Encapsularlos aquí evita el patrón habitual de olvidar uno —normalmente el
 * parcial— y presentar una cifra incompleta como si fuera definitiva.
 */
export interface DataBoundaryProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry?: () => void;

  /** true cuando la consulta terminó bien pero no hay nada que mostrar. */
  isEmpty?: boolean;
  empty?: EmptyStateProps;

  /** Estado global del dato. `PARTIAL` y `NOT_AVAILABLE` producen aviso. */
  status?: DataStatus;
  coverage?: number | null;
  note?: string;
  warnings?: string[];

  skeleton: ReactNode;
  children: ReactNode;
}

export function DataBoundary({
  isLoading,
  isError,
  error,
  onRetry,
  isEmpty = false,
  empty,
  status,
  coverage,
  note,
  warnings,
  skeleton,
  children,
}: DataBoundaryProps): JSX.Element {
  if (isLoading) {
    return (
      <div aria-busy="true" aria-live="polite">
        <span className="sr-only">Cargando información…</span>
        {skeleton}
      </div>
    );
  }

  if (isError) return <ErrorState error={error} onRetry={onRetry} />;

  if (isEmpty) return <EmptyState {...empty} />;

  const showPartial = status === 'PARTIAL' || (warnings !== undefined && warnings.length > 0);

  return (
    <>
      {status === 'NOT_AVAILABLE' && <UnavailableNotice note={note} />}
      {showPartial && status !== 'NOT_AVAILABLE' && (
        <PartialNotice coverage={coverage} note={note} warnings={warnings} />
      )}
      {children}
    </>
  );
}
